import React, { useEffect, useRef, useState } from 'react';
import HBTag from '../HBTag/HBTag';
import { STRUCTURE } from '../HBTag/HBTag.styles';
import HBTooltip from '../HBTooltip/HBTooltip';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { TimezoneRoot } from './HBTimezone.styles';
import { getTimezoneDisplay } from './HBTimezone.utils';
import type { HBTimezoneProps } from './HBTimezone.types';

export type { HBTimezoneFormat, HBTimezoneProps } from './HBTimezone.types';

const TOOLTIP_TARGET_STYLE: React.CSSProperties = {
    display: 'inline-flex',
    verticalAlign: 'baseline'
};

function toTimestamp(date: HBTimezoneProps['date']): number | null {
    if (date === null || date === undefined || date === '') {
        return null;
    }

    const timestamp = new Date(date).getTime();

    // Falling back to "now" would show a plausible but wrong abbreviation across a
    // DST change, so a broken date from the consumer fails loudly instead.
    if (Number.isNaN(timestamp)) {
        throw new Error(`HBTimezone: invalid date "${String(date)}"`);
    }

    return timestamp;
}

/**
 * Shows a timezone as a compact HBTag — `PDT` or `GMT−7` — with the full label
 * ("Pacific Time - US & Canada") in a tooltip.
 */
const HBTimezone = ({
    timezone,
    date,
    format = 'abbreviation',
    size = 'sm',
    className,
    testId,
    testIdName
}: HBTimezoneProps) => {
    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'timezone',
        componentName: 'HBTimezone'
    });
    const rootRef = useRef<HTMLSpanElement>(null);
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);

    const display = getTimezoneDisplay(timezone, toTimestamp(date), format);

    // Escape dismisses the tooltip whether it was opened by focus or by hover
    // (WCAG 1.4.13), so the listener is on the document rather than the tag.
    useEffect(() => {
        if (!isTooltipOpen || isDismissed) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsDismissed(true);
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isTooltipOpen, isDismissed]);

    if (!display) {
        return null;
    }

    const { text, label } = display;
    const hasTooltip = label !== null;

    const tag = (
        <TimezoneRoot
            ref={rootRef}
            className={className}
            $borderRadius={STRUCTURE[size].borderRadius}
            {...(hasTooltip && {
                // The full label is the accessible name, so a screen reader gets it
                // without hovering. `img` makes the visible text presentational:
                // the abbreviation is not read a second time after the name, and
                // `aria-label` is allowed (it is prohibited on a roleless span).
                role: 'img',
                'aria-label': `${label} (${text})`,
                tabIndex: 0,
                'data-tooltip':
                    isTooltipOpen && !isDismissed ? 'open' : 'closed',
                onBlur: () => setIsDismissed(false),
                onMouseLeave: () => {
                    // Keep the dismissal while the tag still has focus, or moving
                    // the pointer off it would bring the tooltip back.
                    if (document.activeElement !== rootRef.current) {
                        setIsDismissed(false);
                    }
                }
            })}
            data-testid={finalTestId}
        >
            <HBTag
                label={text}
                size={size}
                data-testid={finalTestId && `${finalTestId}-tag`}
            />
        </TimezoneRoot>
    );

    if (!hasTooltip) {
        return tag;
    }

    return (
        <HBTooltip
            placement="top"
            target={tag}
            targetContainerStyle={TOOLTIP_TARGET_STYLE}
            forceClose={isDismissed}
            onOpenChange={({ isOpen }) => setIsTooltipOpen(isOpen)}
        >
            {label}
        </HBTooltip>
    );
};

export default HBTimezone;
